import moment from "moment-timezone";
import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Icon from "react-feather";

import APIManager from "../../managers/APIManager";

import Page from "../common/Page";
import Loading from "../common/Loading";

import HelperFormatter, { getCurrencySymbol } from "../../helpers/formatter";

import { Translation, useTranslation } from "react-i18next";
import { capitalizeFirst } from "../../utils/manipulateText";

import Control from "./Control";
import LanguageSelector from "../../components/common/LanguageSelector";

import Notification from "../../components/common/Notification";
import SiteSelector from "../common/SiteSelector";
import MessageText from "../common/MessageText";
import { useNavigate, useParams } from "react-router";

import { SiteActionCreators, SiteActions } from "../../store/actions/site";
import BusinessBreakdownAndFiltersProvider, {
  useBusinessBreakdownAndFilters,
} from "./BusinessBreakdownAndFilters";
import BusinessReportTable from "./BusinessReportTable";

const INITIAL_CHART = (t, currency) => ({
  combinedChart: {
    options: {
      chart: {
        id: "combined-site-revenue",
        type: "line",
        stacked: false,
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            zoom: false,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: false,
          },
          autoSelected: "selection",
        },
        events: {
          mounted: function (chartContext) {
            chartContext.toggleSeries(capitalizeFirst(t("ad req.")));
            chartContext.toggleSeries(capitalizeFirst(t("matched req.")));
          },
        },
      },
      colors: ["#4299e1", "#48bb78", "#FFC107", "#9C27B0"],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: "60%",
        },
      },
      stroke: {
        curve: "smooth",
        width: 4,
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (y, { seriesIndex, w }) => {
            if (
              [capitalizeFirst(t("revenue"))].indexOf(
                w.config.series[seriesIndex].name
              ) !== -1
            ) {
              return HelperFormatter.currency(currency)(y);
            } else if (y) {
              return y.toLocaleString();
            }
          },
        },
      },
      xaxis: {
        type: "datetime",
      },
      yaxis: [
        {
          seriesName: capitalizeFirst(t("revenue")),
          axisTicks: {
            show: true,
          },
          labels: {
            formatter: (val) => {
              if (val >= 1000000) {
                return `${(val / 1000000).toFixed(1)}M`.replace(".0", "");
              } else if (val >= 1000) {
                return `${(val / 1000).toFixed(1)}K`.replace(".0", "");
              }
              return val.toFixed(0);
            },
          },
          title: {
            text:
              capitalizeFirst(t("revenue")) +
              ` (${getCurrencySymbol(currency)})`,
            rotate: -90,
          },
        },
        {
          seriesName: capitalizeFirst(t("impression")),
          opposite: true,
          axisTicks: {
            show: true,
          },
          labels: {
            formatter: (val) => {
              if (val >= 1000000) {
                return `${(val / 1000000).toFixed(1)}M`.replace(".0", "");
              } else if (val >= 1000) {
                return `${(val / 1000).toFixed(1)}K`.replace(".0", "");
              }
              return val;
            },
          },
          title: {
            text: capitalizeFirst(t("impression")),
            rotate: -90,
          },
        },
      ],
      fill: {
        opacity: 1,
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        markers: {
          width: 10,
          height: 10,
        },
        itemMargin: {
          vertical: 4,
        },
      },
      markers: {
        size: 5,
        hover: {
          size: 8,
        },
      },
    },
    series: [
      {
        name: capitalizeFirst(t("revenue")),
        type: "bar",
        data: [],
      },
      {
        name: capitalizeFirst(t("impression")),
        type: "line",
        data: [],
      },
    ],
  },
});
const AD_DATA_COLUMNS = (userCurrency) => ({
  revenue: {
    index: 0,
    code: "revenue",
    color: "#2b6cb0",
    enabled: true,
    hidden: false,
    name: "Revenue",
    padding: "42px",
    formatter: HelperFormatter.currency(userCurrency),
  },
  adRequests: {
    index: 1,
    code: "adRequests",
    color: "#c05621",
    enabled: false,
    hidden: true,
    name: "Ad Requests",
    padding: "42px",
    formatter: HelperFormatter.number,
  },
  matchedRequests: {
    index: 2,
    code: "matchedRequests",
    color: "#b83280",
    enabled: false,
    hidden: true,
    name: "Matched Ad Requests",
    padding: "20px",
    formatter: HelperFormatter.number,
  },
  views: {
    index: 3,
    code: "views",
    color: "#6b46c1",
    enabled: true,
    hidden: true,
    name: "Impressions",
    padding: "42px",
    formatter: HelperFormatter.number,
  },
  clicks: {
    index: 4,
    code: "clicks",
    color: "#b7791f",
    enabled: true,
    hidden: true,
    name: "Clicks",
    padding: "42px",
    formatter: HelperFormatter.number,
  },
  coverage: {
    index: 5,
    code: "coverage",
    color: "#c53030",
    enabled: false,
    hidden: true,
    name: "Coverage",
    padding: "42px",
    formatter: HelperFormatter.percentage,
  },
  ctr: {
    index: 6,
    code: "ctr",
    color: "#2f855a",
    enabled: true,
    hidden: true,
    name: "CTR",
    padding: "42px",
    formatter: HelperFormatter.percentage,
  },
  requestECPM: {
    index: 7,
    code: "requestECPM",
    color: "#4a5568",
    enabled: false,
    hidden: true,
    name: "Request eCPM",
    padding: "20px",
    formatter: HelperFormatter.currency(userCurrency),
  },
  eCPM: {
    index: 8,
    code: "eCPM",
    color: "#2c7a7b",
    enabled: false,
    hidden: true,
    name: "eCPM",
    padding: "42px",
    formatter: HelperFormatter.currency(userCurrency),
  },
  viewabilityReal: {
    index: 9,
    code: "viewabilityReal",
    color: "#4c51bf",
    enabled: true,
    hidden: true,
    name: "Viewability",
    padding: "42px",
    formatter: HelperFormatter.percentage,
  },
});

const BusinessDetail = () => {
  const { t } = useTranslation();

  const params = useParams();
  const siteId = params.site;
  const navigate = useNavigate();

  const auth = useSelector((state) => state.auth);
  const userCurrency = useSelector((state) => state.auth.user.currency);

  const selectedSiteLoading = useSelector(
    (state) => state.site.selectedSiteLoading
  );
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const sites = useSelector((state) => state.site.sites);

  const language = useSelector((state) => state.app.language);
  const dispatch = useDispatch();
  const [refreshReport, setRefreshReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeoutErr, setTimeoutErr] = useState(false);
  const [serverErr, setServerErr] = useState(false);
  const [mergedReport, setMergedReport] = useState({
    breakdown: "day",
    report: [],
  });
  const [chart, setChart] = useState(INITIAL_CHART(t, userCurrency));
  const currencySymbol = getCurrencySymbol(userCurrency);
  const adDataColumns = useMemo(
    () => AD_DATA_COLUMNS(userCurrency),
    [userCurrency]
  );
  const [table, setTable] = useState({ tbl: [], dim: [] });
  const [tableFooter, setTableFooter] = useState({});
  const sortOrder = useRef(null);
  const {
    breakdowns,
    setTableBreakdowns,
    startDate,
    endDate,
    setBusinessCurr,
    reset,
  } = useBusinessBreakdownAndFilters();

  const getReport = () => {
    // if (selectedSite?.id === undefined) {
    //   setLoading(false);
    //   return;
    // }

    setLoading(true);
    APIManager.getBusinessReport(selectedSite?.id, {
      site: breakdowns.includes("site"),
      day: breakdowns.includes("day"),
      month: breakdowns.includes("month"),
      startDate: startDate,
      endDate: endDate,
    }).then((res) => {
      setBusinessCurr(res?.data?.currency?.toLowerCase() || "usd");
      const report = res?.data?.results?.reverse() || [];
      const breakdownKey =
        breakdowns.indexOf("day") !== -1
          ? "day"
          : breakdowns.indexOf("month") !== -1
          ? "month"
          : breakdowns.indexOf("site") !== -1
          ? "site"
          : "";

      const mergedReport = Object.values(
        report.reduce((pv, cv) => {
          let key = "All";
          if (breakdownKey === "day") {
            key =
              moment(cv.day.split(".").reverse().join("-") + " 03:00").unix() *
              1000;
          } else if (breakdownKey === "month") {
            key = cv.month;
          } else if (breakdownKey === "site") {
            key = cv.siteId;
          }
          pv[key] = pv[key] || {
            key: key,
            grossRevenue: 0,
            empowerRevenue: 0,
            businessRevenue: 0,
            netRevenue: 0,

            view: 0,
            request: 0,
          };
          pv[key].grossRevenue += cv.grossSpent;
          pv[key].empowerRevenue += cv.empowerSpent;
          pv[key].businessRevenue += cv.bussinessSpent;
          pv[key].netRevenue += cv.netSpent;

          pv[key].view += cv.view;
          pv[key].request += cv.request;
          return pv;
        }, {})
      );
      const chartRevenue = Object.assign({}, chart.combinedChart);
      chartRevenue.tooltip = { y: [] };
      chartRevenue.options.xaxis.type =
        breakdownKey === "day" ? "datetime" : "string";
      chartRevenue.options.yaxis = [
        {
          seriesName: capitalizeFirst(t("revenue")),
          axisTicks: {
            show: true,
          },
          labels: {
            formatter: (val) => {
              if (val >= 1000000) {
                return `${(val / 1000000).toFixed(1)}M`.replace(".0", "");
              } else if (val >= 1000) {
                return `${(val / 1000).toFixed(1)}K`.replace(".0", "");
              }
              return val.toFixed(0);
            },
          },
          title: {
            text: capitalizeFirst(t("revenue")) + ` (${currencySymbol})`,
            rotate: -90,
          },
        },
        {
          seriesName: capitalizeFirst(t("impression")),
          opposite: true,
          axisTicks: {
            show: true,
          },
          labels: {
            formatter: (val) => {
              if (val >= 1000000) {
                return `${(val / 1000000).toFixed(1)}M`.replace(".0", "");
              } else if (val >= 1000) {
                return `${(val / 1000).toFixed(1)}K`.replace(".0", "");
              }
              return val;
            },
          },
          title: {
            text: capitalizeFirst(t("impression")),
            rotate: -90,
          },
        },
      ];
      chartRevenue.series = [];
      chartRevenue.series = [
        {
          name: capitalizeFirst(t("revenue")),
          type: "bar",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: Number(
              result.netRevenue.toFixed(Number(result.netRevenue) > 1 ? 0 : 2)
            ),
          })),
        },
        {
          name: capitalizeFirst(t("impression")),
          type: "line",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: result.view,
          })),
        },
        {
          name: capitalizeFirst(t("ad req.")),
          type: "line",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: result.request,
          })),
          yAxisIndex: 2,
        },
        {
          name: capitalizeFirst(t("matched req.")),
          type: "line",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: result.match,
          })),
          yAxisIndex: 3,
        },
      ];
      chartRevenue.tooltip.y.push({
        formatter: adDataColumns.revenue.formatter,
      });

      setChart({ ...chart, combinedChart: chartRevenue });

      setMergedReport({ breakdown: breakdownKey, report: mergedReport });

      var dimensions = [];
      if (res.data.results?.length) {
        const allBreakdowns = ["site", "month", "day"];
        var grandTotal = {};

        const keys = [];
        const bdKeys = [];
        Object.keys(report[0]).forEach((key) => {
          if (key === "monthFormatted" || key === "resultSize") return;
          else if (allBreakdowns.indexOf(key) === -1) keys.push(key);
          else bdKeys.push(key);
        });
        grandTotal = { ...res.data.total };
        bdKeys.forEach((key) => (grandTotal[key] = ""));

        if (bdKeys.indexOf("site") !== -1) {
          grandTotal.site = "";
        }

        grandTotal[breakdownKey] = "grand total";
        setTableFooter({ ...grandTotal });
        dimensions = [...bdKeys, ...keys];
        setTableBreakdowns([...dimensions]);
      }
      const tmp = [].concat(res.data.results).reverse();

      setTable({
        ...table,
        tbl: tmp,
        dim: dimensions,
      });

      setLoading(false);
    });
  };

  useEffect(() => {
    setLoading(true);
    setTimeoutErr(false);
    setServerErr(false);
    setRefreshReport((prev) => !prev);
  }, [language]);

  useEffect(() => {
    if (siteId) {
      dispatch(SiteActionCreators.getSite(siteId));
    }
  }, [siteId]);

  useEffect(() => {
    getReport();
  }, [refreshReport, selectedSite?.id]);

  const toggleFilters = () => {
    const filters = document.querySelector(".filters");
    filters.classList.toggle("hidden");
    filters.classList.toggle("flex");
    filters.classList.toggle("flex-row");
  };

  var pageContent = null;

  if (!loading) {
    if (selectedSite?.onboardingStep >= 0) {
      navigate(`/sites/${selectedSite?.id}/onboarding`);
      return null;
    }

    const adRequestData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.request,
    }));
    const matchRequestData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.match,
    }));
    const viewsData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.view,
    }));
    const totalRevenueData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.netRevenue,
    }));
    if (!totalRevenueData.length) {
      pageContent = (
        <div className="py-4 text-orange-600 text-md text-center">
          <Icon.Info className="inline mr-2" width="16" height="16" />
          <Translation>
            {(t) =>
              capitalizeFirst(
                t("there is no data to show for this time period.")
              )
            }
          </Translation>
        </div>
      );
    } else {
      pageContent = (
        <>
          {/* <div className="md:grid md:grid-cols-5 md:gap-4 mb-5">
            <div className="grid grid-cols-2 gap-2 md:gap-4 md:grid-cols-1 md:col-span-1">
              <StatSection
                totalRevenueData={totalRevenueData}
                adDataColumns={adDataColumns}
                mergedReport={mergedReport}
                adRequestData={adRequestData}
                matchRequestData={matchRequestData}
                viewsData={viewsData}
              />
            </div>
            <div className="md:col-span-4">
              <Card title="revenue & impression" type="chart">
                <div className="p-2">
                  <ComboChart chart={chart} />
                </div>
              </Card>
            </div>
          </div> */}
          <BusinessReportTable
            table={table}
            site={selectedSite}
            tableFooter={tableFooter}
            sortOrder={sortOrder}
            setTable={setTable}
          />
        </>
      );
    }
  }

  return (
    <>
      <Page
        controlClass="w-full items-center justify-between"
        title={
          selectedSite &&
          sites?.length > 0 && (
            <>
              <div className="flex justify-between relative my-1">
                <SiteSelector
                  onSelect={(otherSite) => {
                    reset();
                    navigate(`/business/${otherSite.id}`);
                  }}
                />
                <div className="md:hidden flex">
                  <button className={"filter-icon"} onClick={toggleFilters}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.29289 1.29289C1.10536 1.48043 1 1.73478 1 2V4.586C1.00006 4.85119 1.10545 5.10551 1.293 5.293L7.707 11.707C7.89455 11.8945 7.99994 12.1488 8 12.414V19L12 15V12.414C12.0001 12.1488 12.1055 11.8945 12.293 11.707L18.707 5.293C18.8946 5.10551 18.9999 4.85119 19 4.586V2C19 1.73478 18.8946 1.48043 18.7071 1.29289C18.5196 1.10536 18.2652 1 18 1H2C1.73478 1 1.48043 1.10536 1.29289 1.29289Z"
                        stroke="#728195"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <div className="hidden md:flex flex-row items-center justify-end w-72 divide-x">
                  {/* <QuickFilters
                    sites={sites}
                    setRefreshReport={setRefreshReport}
                    handleClick={(item) => {
                      setBreakdowns(item?.breakdowns || ["daily"]);
                      setPlatformFilter(item?.platform || "all");
                      setAdPlacements(item?.placements || []);
                      onDateRangeChange(item?.dateRange);
                    }}
                    handleRedirect={(siteId) => {
                      return `/sites/${siteId}`;
                    }}
                  /> */}
                  <LanguageSelector customStyle="ml-2 float-right" />
                </div>
              </div>
              <Notification data={selectedSite} />
            </>
          )
        }
        controls={
          <Control
            loading={loading}
            onRefreshReport={() => {
              setRefreshReport((prev) => !prev);
            }}
          />
        }
      >
        {timeoutErr ? (
          <MessageText
            message={
              "Your report took a long time to generate. You can check its status on the reports page."
            }
          />
        ) : serverErr ? (
          <MessageText
            message={
              "A server side error has occured. Please try refreshing the report again."
            }
          />
        ) : (
          <>
            {(loading || selectedSiteLoading) && <Loading />}
            {pageContent}
          </>
        )}
      </Page>
    </>
  );
};

export default () => (
  <BusinessBreakdownAndFiltersProvider>
    <BusinessDetail />
  </BusinessBreakdownAndFiltersProvider>
);
